import { useState } from 'react';

export type UseAsyncOptions<T> = {
  onError?: (error: Error) => void;
  defaultValue?: T;
};

export type AsyncFunction<V> = (...args: never[]) => PromiseLike<V>;
export type AsyncReturnType<V> = V extends AsyncFunction<infer U> ? U : V;

export const useAsync = <
  // eslint-disable-next-line
  T extends (...args: any[]) => Promise<any>,
  V = AsyncReturnType<T>,
>(
  serviceImplementation: T,
  options?: UseAsyncOptions<Awaited<ReturnType<T>>>
) => {
  const [state, setState] = useState({
    isLoading: false,
    isCompleted: false,
    isRequested: false
  });
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<V | null>(options?.defaultValue ?? null);

  const run = async (...params: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
    setState({
      ...state,
      isLoading: true,
      isRequested: true
    });
    setError(null);
    try {
      const response = await serviceImplementation(...params);
      setData(response);

      setState({
        ...state,
        isLoading: false,
        isRequested: true,
        isCompleted: true
      });

      return response;
    } catch (err) {
      setError(err as Error);
      setState({
        ...state,
        isLoading: false,
        isCompleted: true
      });

      if (options?.onError) {
        options.onError(err as Error);
      }
      throw err;
    }
  };

  const set = (value: V | null) => {
    setData(value);
  };

  return {
    isLoading: state.isLoading,
    run,
    data,
    error,
    isRequested: state.isRequested,
    isCompleted: state.isCompleted,
    set
  };
};